import { CABECALHO_CSRF } from './auth';
import type { Sessao } from './auth';
import type { Resultado } from './acesso';
import { buscar } from './buscar';

/**
 * CONFERÊNCIA DA GELADEIRA (dono 19/10/2026) — o front de /api/geladeira.
 * O Iggor conta sem ver o HOS; o dono, o Carlos e os admins veem o painel, as vendas (vendedor,
 * cliente, pagamento, parcelas), o histórico e mexem na lista.
 */

export interface ContagemDaGeladeira {
  readonly produtoId: string;
  readonly codigoHos: string;
  readonly nome: string;
  readonly farmacia: number;
  readonly estoque: number;
  readonly total: number;
  readonly hosSaldo: number | null;
  readonly hosLidoEm: string | null;
  readonly diferenca: number | null;
}
export interface ConferenciaDaGeladeira {
  readonly id: string;
  readonly dia: string;
  readonly por: { readonly accountId: string; readonly nome: string };
  readonly iniciadaEm: string | null;
  readonly fechadaEm: string;
  readonly movCursor: number | null;
  readonly hosLido: boolean;
  readonly itens: number;
  readonly itensDivergentes: number;
  readonly observacao: string | null;
  readonly contagens: readonly ContagemDaGeladeira[];
}
export interface ParaConferir {
  readonly dia: string;
  readonly produtos: readonly {
    readonly id: string;
    readonly nome: string;
    readonly descricaoHos: string | null;
  }[];
  readonly ultima: ConferenciaDaGeladeira | null;
  /** O que da lista foi vendido no dia anterior (quantidade e quem vendeu — nunca o saldo). */
  readonly vendidosOntem?: {
    readonly dia: string;
    readonly itens: readonly {
      readonly produtoId: string;
      readonly nome: string;
      readonly quantidade: number;
      readonly vendas: readonly {
        readonly instante: string;
        readonly quantidade: number;
        readonly vendedor: string | null;
      }[];
    }[];
  };
}

export type SituacaoDaLinha = 'BATEU' | 'SOBRA' | 'FALTA' | 'HOS_SEM_LEITURA' | 'SEM_CONTAGEM';
export interface MexidaDaGeladeira {
  readonly idHos: number;
  readonly codigoHos: string;
  readonly dia: string;
  readonly hora: string | null;
  readonly tipo: string;
  readonly subtipo: string | null;
  readonly delta: number;
  readonly documento: number | null;
  readonly fornecedor: string | null;
  readonly classe: 'venda' | 'informativo' | 'suspeito';
}
export interface LinhaDoPainel {
  readonly produtoId: string;
  readonly codigoHos: string;
  readonly nome: string;
  readonly descricaoHos: string | null;
  readonly contagem: {
    readonly farmacia: number;
    readonly estoque: number;
    readonly total: number;
    readonly hosNaContagem: number | null;
    readonly diferenca: number | null;
  } | null;
  readonly vendeu: number;
  readonly entrou: number;
  readonly mexeuSemVenda: number;
  readonly mexidasSemVenda: number;
  readonly deveriaTer: number | null;
  readonly hosAgora: number | null;
  readonly situacao: SituacaoDaLinha;
  readonly vendasDoDia: { readonly quantidade: number; readonly valor: number };
}
export interface PainelDaGeladeira {
  readonly dia: string;
  readonly hoje: boolean;
  readonly conferencia: Omit<ConferenciaDaGeladeira, 'contagens'> | null;
  readonly hosLidoEm: string | null;
  readonly linhas: readonly LinhaDoPainel[];
  readonly totais: {
    readonly produtos: number;
    readonly bateram: number;
    readonly comDiferenca: number;
    readonly mexidasSemVenda: number;
    readonly vendidas: number;
    readonly valorVendido: number;
    readonly unidadesNoHos: number | null;
  };
  readonly mexidas: readonly MexidaDaGeladeira[];
}

export interface FormaDePagamento {
  readonly forma: string;
  readonly rotulo: string;
  readonly parcelas: number;
  readonly valor: number;
  readonly valorDaParcela: number | null;
  readonly cartoes: readonly {
    readonly valor: number;
    readonly bandeira: string | null;
    readonly final: string | null;
    readonly autorizacao: string | null;
  }[];
}
export interface VendaDaGeladeira {
  readonly chave: string;
  readonly dia: string;
  readonly instante: string;
  readonly idVenda: number;
  readonly cupom: number | null;
  readonly codigoHos: string;
  readonly nome: string | null;
  readonly descricao: string | null;
  readonly quantidade: number;
  readonly valor: number;
  readonly valorCupom: number;
  readonly vendedor: { readonly codigo: number | null; readonly nome: string | null };
  readonly cliente: { readonly codigo: number | null; readonly nome: string | null };
  readonly pagamentoResumo: string | null;
  readonly detalhe: {
    readonly numeroCaixa?: number | null;
    readonly teleEntrega?: boolean;
    readonly pagamento?: readonly FormaDePagamento[];
    readonly cliente?: { readonly cpfNaNota?: boolean };
    readonly itens?: readonly {
      readonly alvo: boolean;
      readonly produto: { readonly descricao: string | null };
      readonly quantidade: number;
      readonly valor: number;
    }[];
  } | null;
}
export interface GrupoDeVendas {
  readonly nome: string;
  readonly quantidade: number;
  readonly valor: number;
  readonly cupons: number;
}
export interface VendasDaGeladeira {
  readonly de: string;
  readonly ate: string;
  readonly resumo: {
    readonly quantidade: number;
    readonly valor: number;
    readonly cupons: number;
    readonly porProduto: readonly GrupoDeVendas[];
    readonly porVendedor: readonly GrupoDeVendas[];
    readonly porPagamento: readonly GrupoDeVendas[];
  };
  readonly vendas: readonly VendaDaGeladeira[];
}
export interface HistoricoDaGeladeira {
  readonly de: string;
  readonly ate: string;
  readonly conferencias: readonly ConferenciaDaGeladeira[];
  readonly mexidas: readonly MexidaDaGeladeira[];
}
export interface ProdutoDaGeladeira {
  readonly id: string;
  readonly codigoHos: string;
  readonly nome: string;
  readonly descricaoHos: string | null;
  readonly ordem: number;
  readonly ativo: boolean;
  readonly estoqueCatalogo: number | null;
}
export interface ProdutoDoCatalogo {
  readonly codigoHos: string;
  readonly descricao: string;
  readonly fabricante: string | null;
  readonly estoque: string | null;
}

async function chamar<T>(
  caminho: string,
  o: { metodo?: 'GET' | 'POST' | 'PATCH'; corpo?: unknown; sessao?: Sessao } = {},
): Promise<Resultado<T>> {
  const metodo = o.metodo ?? 'GET';
  let r: Response;
  try {
    r = await buscar(caminho, {
      method: metodo,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(metodo !== 'GET' ? { 'content-type': 'application/json' } : {}),
        ...(o.sessao ? { [CABECALHO_CSRF]: o.sessao.csrfToken } : {}),
      },
      ...(metodo !== 'GET' ? { body: JSON.stringify(o.corpo ?? {}) } : {}),
    });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok) return { ok: true, dados: (await r.json()) as T };
  let mensagem = '';
  try {
    mensagem = ((await r.json()) as { error?: { message?: string } }).error?.message ?? '';
  } catch {
    // sem corpo
  }
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  if (r.status === 409) return { ok: false, falha: { tipo: 'conflito', mensagem } };
  if (r.status === 400 || r.status === 404)
    return { ok: false, falha: { tipo: 'invalido', mensagem } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

const q = (o: Record<string, string | null | undefined>) =>
  new URLSearchParams(Object.entries(o).filter((e): e is [string, string] => !!e[1])).toString();

export const lerParaConferir = () => chamar<ParaConferir>('/api/geladeira/conferir');
export const fecharConferencia = (
  sessao: Sessao,
  corpo: {
    chave: string;
    iniciadaEm: string | null;
    observacao: string | null;
    itens: { produtoId: string; farmacia: number; estoque: number }[];
  },
) =>
  chamar<ConferenciaDaGeladeira>('/api/geladeira/conferencias', { metodo: 'POST', corpo, sessao });
export const lerPainel = (dia?: string | null) =>
  chamar<PainelDaGeladeira>(`/api/geladeira/painel?${q({ dia })}`);
export const lerVendas = (f: {
  de: string;
  ate: string;
  produto?: string | null;
  vendedor?: string | null;
}) => chamar<VendasDaGeladeira>(`/api/geladeira/vendas?${q(f)}`);
export const lerHistorico = (de: string, ate: string) =>
  chamar<HistoricoDaGeladeira>(`/api/geladeira/historico?${q({ de, ate })}`);
export const lerProdutos = () =>
  chamar<{ produtos: readonly ProdutoDaGeladeira[] }>('/api/geladeira/produtos');
export const buscarNoCatalogo = (termo: string) =>
  chamar<{ produtos: readonly ProdutoDoCatalogo[] }>(
    `/api/geladeira/produtos/buscar?${q({ q: termo })}`,
  );
export const incluirProduto = (sessao: Sessao, codigoHos: string, nome: string) =>
  chamar<ProdutoDaGeladeira>('/api/geladeira/produtos', {
    metodo: 'POST',
    corpo: { codigoHos, nome },
    sessao,
  });
export const alterarProduto = (
  sessao: Sessao,
  id: string,
  m: { nome?: string; ordem?: number; ativo?: boolean },
) =>
  chamar<ProdutoDaGeladeira>(`/api/geladeira/produtos/${encodeURIComponent(id)}`, {
    metodo: 'PATCH',
    corpo: m,
    sessao,
  });

/* ------------------------------------------------------------------ *
 * Formatos (puros, testados)
 * ------------------------------------------------------------------ */

export const qtd = (n: number) =>
  Number.isInteger(n) ? String(n) : n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
export const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
export const sinal = (n: number) => (n > 0 ? `+${qtd(n)}` : n < 0 ? `−${qtd(-n)}` : '0');

/** "14:32" no horário de Brasília. */
export const horaBrasilia = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  });
/** "seg, 19/10" */
export const diaCurto = (dia: string) => {
  const d = new Date(`${dia}T12:00:00Z`);
  const s = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][d.getUTCDay()] ?? '';
  return `${s}, ${dia.slice(8, 10)}/${dia.slice(5, 7)}`;
};
/** "MARIA S OLIVEIRA" → "Maria S Oliveira" (o HOS grava em maiúscula). */
export function nomeBonito(n: string | null | undefined): string | null {
  if (!n) return null;
  const s = n.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!s) return null;
  return s
    .replace(/(^|[\s'-])(\p{L})/gu, (_, a: string, b: string) => a + b.toUpperCase())
    .replace(/\b(Da|De|Do|Das|Dos|E)\b/g, (x) => x.toLowerCase());
}

/** Rótulo do tipo do razão do HOS. */
export const ROTULO_DA_MEXIDA: Readonly<Record<string, string>> = {
  compra: 'Entrada de nota',
  bonificacao: 'Bonificação',
  reserva_ecommerce: 'Reserva do site',
  devolucao_fornecedor: 'Devolução ao fornecedor',
  perda: 'Perda',
  consumo: 'Consumo interno',
  brinde: 'Brinde',
  estorno: 'Estorno',
  contagem: 'Ajuste de contagem',
  estoque_inicial: 'Estoque inicial',
  nf_saida: 'Nota de saída',
  outro: 'Lançamento sem tipo',
};

/** A situação de uma linha, em palavras (estado nunca é só cor). */
export function textoDaSituacao(l: Pick<LinhaDoPainel, 'situacao' | 'contagem'>): string {
  const d = l.contagem?.diferenca ?? 0;
  if (l.situacao === 'BATEU') return 'Bateu';
  if (l.situacao === 'SOBRA') return `Sobra ${qtd(d)}`;
  if (l.situacao === 'FALTA') return `Falta ${qtd(-d)}`;
  if (l.situacao === 'HOS_SEM_LEITURA') return 'HOS sem leitura';
  return 'Não contado';
}

/** A contagem do Iggor em andamento fica no aparelho: fechou o app sem querer, não perde. */
export const CHAVE_DO_RASCUNHO = 'inovapay.geladeira.rascunho';
export interface Rascunho {
  readonly dia: string;
  readonly chave: string;
  readonly iniciadaEm: string;
  readonly valores: Record<string, { farmacia: string; estoque: string }>;
}
export function lerRascunho(dia: string): Rascunho | null {
  try {
    const r = JSON.parse(localStorage.getItem(CHAVE_DO_RASCUNHO) ?? 'null') as Rascunho | null;
    return r && r.dia === dia ? r : null;
  } catch {
    return null;
  }
}
export function gravarRascunho(r: Rascunho | null): void {
  try {
    if (r) localStorage.setItem(CHAVE_DO_RASCUNHO, JSON.stringify(r));
    else localStorage.removeItem(CHAVE_DO_RASCUNHO);
  } catch {
    // sem armazenamento (aba anônima): segue sem rascunho
  }
}
/** "2" → 2; "" → null; "2,5" → 2.5; lixo → NaN (a tela recusa). */
export function lerNumero(t: string): number | null {
  const s = t.trim().replace(',', '.');
  if (!s) return null;
  if (!/^\d{1,4}(\.\d{1,3})?$/.test(s)) return Number.NaN;
  return Number(s);
}
